/**
 * Page /grc/cahier — Cahier de l'accueil (GRC).
 *
 * Cahier de liaison numérique de l'accueil : rappels, pense-bêtes, infos à
 * transmettre. Remplace le cahier papier. Accès GRC + admin/maire.
 *
 * Les vrais messages personnels d'un administré passent par la messagerie
 * interne ; ce cahier sert aux notes de service. Chaque note a une catégorie,
 * un statut (à faire / fait) et peut être affectée à un service.
 */

import { useEffect, useState, useCallback } from 'react';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api } from '@/lib/api';
import type { CahierNote } from '@/lib/api';
import { NotebookPen, Plus, Check, RotateCcw, Trash2, AlertCircle, ListTodo } from 'lucide-react';

const CATEGORIES: { value: CahierNote['categorie']; label: string; tone: string }[] = [
  { value: 'rappel', label: 'Rappel', tone: 'oklch(0.70 0.15 230)' },
  { value: 'info', label: 'Info', tone: 'oklch(0.66 0.15 175)' },
  { value: 'a_transmettre', label: 'À transmettre', tone: 'oklch(0.76 0.16 60)' },
  { value: 'urgent', label: 'Urgent', tone: 'oklch(0.68 0.20 25)' },
];

const SERVICES: { value: string; label: string }[] = [
  { value: '', label: 'Aucun service' },
  { value: 'service_technique', label: 'Service technique' },
  { value: 'scolaire_cantine', label: 'Scolaire & cantine' },
  { value: 'petite_enfance', label: 'Petite enfance' },
  { value: 'urbanisme', label: 'Urbanisme' },
  { value: 'associations', label: 'Associations' },
  { value: 'grc', label: 'Accueil / GRC' },
  { value: 'rh', label: 'Ressources humaines' },
];

function catMeta(c: CahierNote['categorie']) {
  return CATEGORIES.find((x) => x.value === c) ?? CATEGORIES[1]!;
}
function serviceLabel(s: string | null): string | null {
  if (!s) return null;
  return SERVICES.find((x) => x.value === s)?.label ?? s;
}
function dateTimeFr(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function GrcCahierPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [notes, setNotes] = useState<CahierNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<'a_faire' | 'fait' | 'toutes'>('a_faire');

  // Formulaire de création
  const [showCreate, setShowCreate] = useState(false);
  const [contenu, setContenu] = useState('');
  const [categorie, setCategorie] = useState<CahierNote['categorie']>('info');
  const [assigneService, setAssigneService] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statut = filtre === 'toutes' ? undefined : filtre;
      const r = await api.grc.cahier.list(statut);
      setNotes(r.notes);
    } catch {
      setError('Impossible de charger le cahier.');
    } finally {
      setLoading(false);
    }
  }, [filtre]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitCreate = useCallback(async () => {
    if (!contenu.trim()) return;
    setSaving(true);
    try {
      await api.grc.cahier.create({
        contenu: contenu.trim(),
        categorie,
        assigneService: assigneService || null,
      });
      setContenu('');
      setCategorie('info');
      setAssigneService('');
      setShowCreate(false);
      await load();
    } catch {
      setError('La note n\'a pas pu être enregistrée.');
    } finally {
      setSaving(false);
    }
  }, [contenu, categorie, assigneService, load]);

  const toggleFait = useCallback(
    async (n: CahierNote) => {
      try {
        await api.grc.cahier.update(n.id, { statut: n.statut === 'fait' ? 'a_faire' : 'fait' });
        await load();
      } catch {
        setError('Action impossible.');
      }
    },
    [load]
  );

  const remove = useCallback(
    async (n: CahierNote) => {
      const ok = await confirm({
        title: 'Supprimer cette note ?',
        description: 'Elle sera définitivement retirée du cahier.',
        confirmLabel: 'Supprimer',
        danger: true,
      });
      if (!ok) return;
      try {
        await api.grc.cahier.remove(n.id);
        await load();
      } catch {
        setError('Suppression impossible.');
      }
    },
    [confirm, load]
  );

  return (
    <GrcLayout active="cahier">
      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5">
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={NotebookPen} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">GRC & communication</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Cahier de l'accueil
            </h1>
          </div>
          <Button type="button" variant="primary" size="sm" className="ml-auto" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Nouvelle note</span>
          </Button>
        </div>

        <p className="mb-4 max-w-2xl text-sm text-ink-muted">
          Rappels, pense-bêtes et infos à transmettre — le cahier de liaison de l'accueil, partagé
          par le service. Les messages personnels d'un administré se notent plutôt dans la messagerie interne.
        </p>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Filtres */}
        <div className="mb-4 flex flex-wrap gap-2">
          {([
            { k: 'a_faire', label: 'À faire' },
            { k: 'fait', label: 'Faites' },
            { k: 'toutes', label: 'Toutes' },
          ] as const).map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFiltre(f.k)}
              className={`rounded-r-pill border px-3 py-1.5 text-xs font-medium transition ${
                filtre === f.k
                  ? 'border-grc/30 bg-grc-tint/80 text-grc-fg'
                  : 'border-border bg-transparent text-ink-muted hover:bg-surface/40 hover:text-ink'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent" role="status" aria-label="Chargement" />
            </div>
          </Card>
        ) : notes.length === 0 ? (
          <Card variant="glass" padded>
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <ListTodo className="h-8 w-8 text-ink-subtle" aria-hidden="true" />
              <p className="text-sm text-ink-muted">
                {filtre === 'fait' ? 'Aucune note faite pour le moment.' : 'Le cahier est vide. Ajoutez une première note.'}
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => {
              const cat = catMeta(n.categorie);
              const fait = n.statut === 'fait';
              const svc = serviceLabel(n.assigneService);
              return (
                <Card key={n.id} variant="glass" className={`p-3.5 ${fait ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 h-3 w-1 flex-shrink-0 rounded-full" style={{ background: cat.tone }} aria-hidden="true" />
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span
                          className="rounded-r-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ background: `color-mix(in oklab, ${cat.tone} 18%, transparent)`, color: cat.tone }}
                        >
                          {cat.label}
                        </span>
                        {svc && (
                          <Badge variant="neutral" size="sm">
                            {svc}
                          </Badge>
                        )}
                        {fait && (
                          <span className="text-[10px] text-success">
                            Fait{n.faitParNom ? ` · ${n.faitParNom}` : ''}
                          </span>
                        )}
                      </div>
                      <p className={`whitespace-pre-wrap text-sm text-ink ${fait ? 'line-through' : ''}`}>{n.contenu}</p>
                      <div className="mt-1.5 text-[11px] text-ink-subtle">
                        {n.creeParNom ? `${n.creeParNom} · ` : ''}{dateTimeFr(n.createdAt)}
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void toggleFait(n)}
                        title={fait ? 'Rouvrir' : 'Marquer faite'}
                        className="flex h-8 w-8 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-surface hover:text-ink"
                      >
                        {fait ? <RotateCcw className="h-4 w-4" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(n)}
                        title="Supprimer"
                        className="flex h-8 w-8 items-center justify-center rounded-r-md border border-border text-ink-muted transition hover:bg-urgent/10 hover:text-urgent"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Modale de création */}
      <Dialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Nouvelle note du cahier"
        size="md"
      >
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="cahier-contenu">
              Note
            </label>
            <textarea
              id="cahier-contenu"
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              rows={3}
              placeholder="Ex. Rappeler M. Dupont pour le dossier de salle / Pense-bête : commander des enveloppes…"
              className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-grc focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="cahier-cat">
                Catégorie
              </label>
              <select
                id="cahier-cat"
                value={categorie}
                onChange={(e) => setCategorie(e.target.value as CahierNote['categorie'])}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:border-grc focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-muted" htmlFor="cahier-svc">
                À transmettre à (optionnel)
              </label>
              <select
                id="cahier-svc"
                value={assigneService}
                onChange={(e) => setAssigneService(e.target.value)}
                className="w-full rounded-r-md border border-border-hi bg-surface/80 px-3 py-2 text-sm text-ink focus:border-grc focus:outline-none"
              >
                {SERVICES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Annuler
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={() => void submitCreate()} disabled={saving || !contenu.trim()}>
              {saving ? 'Enregistrement…' : 'Ajouter au cahier'}
            </Button>
          </div>
        </div>
      </Dialog>

      {dialogElement}
    </GrcLayout>
  );
}
